// Vendor imports
const Promise = require('bluebird')
const _ = require('lodash')
// Shared imports
const { ExtendableError } = require('shared/error-utils')
// Client lib imports
const airbrake = require('client/lib/airbrake')
const { send: ajax } = require('client/lib/request')
// Client V2 imports
const { NextPageStateEnum } = require('client-v2/common/utils/enums')
// Sibling imports
const { slideDidBecomeVisible } = require('./slide-actions')

// Magic values
const PAGINATION_THRESHOLD = 5 // Load next page when 5 from the end

// Action types

const HIDE = 'ARTICLE_PROFILE_MODAL_HIDE'
const NEXT_PAGE_LOADED = 'ARTICLE_PROFILE_NEXT_PAGE_LOADED'
const NEXT_PAGE_LOADING = 'ARTICLE_PROFILE_NEXT_PAGE_LOADING'
const SET_CURRENT_INDEX = 'ARTICLE_PROFILE_MODAL_SET_CURRENT_INDEX'
const SHOW = 'ARTICLE_PROFILE_MODAL_SHOW'

// Action creators

function setCurrentIndex(currentIndex) {
  return {
    type: SET_CURRENT_INDEX,
    payload: { currentIndex }
  }
}

// Action dispatchers

function hide() {
  return function (dispatch) {
    dispatch(resetHistory)
    dispatch({
      type: HIDE
    })
  }
}

function nextSlide() {
  return function (dispatch, getState) {
    const state = getState().articleProfileModal
    if (state) {
      const { currentIndex, slides } = state
      const nextIndex = currentIndex + 1
      if (nextIndex < slides.size) {
        dispatch(setCurrentIndex(nextIndex))
        dispatch(currentIndexDidChange())
      }
    }
  }
}

function previousSlide() {
  return function (dispatch, getState) {
    const state = getState().articleProfileModal
    if (state) {
      const { currentIndex } = state
      if (currentIndex > 0) {
        const previousIndex = currentIndex - 1
        dispatch(setCurrentIndex(previousIndex))
        dispatch(currentIndexDidChange())
      }
    }
  }
}

function show(options) {
  return function (dispatch) {
    dispatch({
      type: SHOW,
      payload: _.pick(normalizeShowOptions(options), [
        'articlesNeedFetch',
        'initialIndex',
        'items',
        'meta',
        'nextPageCallback'
      ])
    })
    dispatch(currentIndexDidChange({
      pageContext: options.pageContext
    }))
  }
}

// Action helpers

function currentIndexDidChange(options = {}) {
  return function (dispatch, getState) {
    const state = getState().articleProfileModal
    if (state) {
      const slide = state.getCurrentSlide()
      dispatch(updateHistory)
      dispatch(slideDidBecomeVisible({
        slide,
        pageContext: options.pageContext
      }))
      dispatch(maybeNextPage)
    }
  }
}

// Pagination helpers

class NextPageRunoutError extends ExtendableError {
  constructor() {
    super('There is no more content available in this stream.')
    this.name = 'NextPageRunoutError' // Preserve name after minifying
  }
}

function maybeNextPage(dispatch, getState) {
  const state = getState().articleProfileModal
  if (state && state.nextPageState === NextPageStateEnum.AVAILABLE) {
    const { currentIndex, slides } = state
    const paginateIndex = slides.size - PAGINATION_THRESHOLD
    if (currentIndex > paginateIndex) {
      dispatch(fetchNextPage)
    }
  }
}

function fetchNextPage(dispatch, getState) {
  const state = getState().articleProfileModal
  if (state && typeof state.nextPageCallback === 'function') {
    dispatch({ type: NEXT_PAGE_LOADING })
    Promise.resolve(state.nextPageCallback())
    // Treat all pagination errors as the end of the stream
    .catch(() => ({ items: [], nextPageAvailable: false }))
    .then(nextPageLoaded)
    .then(dispatch)
    .catch(err => { airbrake.notify(err) })
  }
}

function nextPageLoaded(payload) {
  return {
    type: NEXT_PAGE_LOADED,
    payload: _.pick(payload, [
      'items',
      'meta',
      'nextPageAvailable'
    ])
  }
}

function normalizeShowOptions(options) {
  const normalized = _.clone(options)
  const { nextUrl } = options.meta || {}
  if (!options.nextPageCallback && nextUrl) {
    // Naïve nextUrl pagination within the modal
    normalized.nextPageCallback = nextUrlCallback(nextUrl)
  }
  return normalized
}

function nextUrlCallback(initialNextUrl) {
  let nextUrl = initialNextUrl
  return function () {
    if (!nextUrl) {
      return Promise.reject(new NextPageRunoutError)
    }
    return ajax({ url: nextUrl, method: 'get' })
    .then(({ items, meta }) => {
      nextUrl = meta && meta.nextUrl
      return {
        items,
        meta,
        nextPageAvailable: !!nextUrl
      }
    })
  }
}

// History helpers

let popstateHandler

function updateHistory(dispatch, getState) {
  const state = getState().articleProfileModal
  if (state) {
    const slidePageHistory = state.getCurrentSlidePageHistory()
    if (state.callerPageHistory.isCurrentState()) {
      slidePageHistory.pushState()
      if (!popstateHandler) {
        popstateHandler = () => dispatch(hide())
        window.addEventListener('popstate', popstateHandler)
      }
    } else {
      slidePageHistory.replaceState()
    }
  }
}

function resetHistory(dispatch, getState) {
  const state = getState().articleProfileModal
  if (state) {
    state.callerPageHistory.pushState()
    // Revert pageContext because the modal is not a referrer
    state.callerPageHistory.revertPageContext()
  }
  if (popstateHandler) {
    window.removeEventListener('popstate', popstateHandler)
    popstateHandler = null
  }
}

// Action exports

module.exports = {
  // Types
  HIDE,
  NEXT_PAGE_LOADED,
  NEXT_PAGE_LOADING,
  SET_CURRENT_INDEX,
  SHOW,
  // Actions
  currentIndexDidChange,
  hide,
  nextSlide,
  previousSlide,
  show,
  // Helpers
  NextPageRunoutError
}
